import { Link } from 'wouter';
import { SignInButton, useUser } from '@clerk/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useListPicks } from '@workspace/api-client-react';
import {
  Brain, TrendingUp, Wallet, Bell, ArrowRight, Sparkles,
  CheckCircle2, ChevronRight, BarChart2, Bookmark, MessageSquare, Share2,
  Zap, Shield, Clock,
} from 'lucide-react';
import { PlatformBadge } from '@/components/platform-badge';
import { RecommendationBadge } from '@/components/recommendation-badge';
import { ConfidenceBar } from '@/components/ui/confidence-bar';
import { Spinner } from '@/components/ui/spinner';
import { DemoAnalysisModal } from '@/components/demo-analysis-modal';
import { useReferralCapture } from '@/hooks/use-referral';

// ─── Static demo analysis data ──────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { useUtmCapture, CAMPAIGN_URLS, getStoredUtmParams } from '@/hooks/use-utm';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

const DEMO_ANALYSIS = {
  marketQuestion: "Will the Fed cut interest rates at least once before December 2025?",
  platform: "polymarket" as const,
  recommendation: "YES" as const,
  confidence: 74,
  riskLevel: "Medium" as const,
  yesPrice: 0.68,
  noPrice: 0.32,
  edgeSummary:
    "Current 68¢ YES price underweights recent dovish Fed signals and cooling inflation data. Implied probability should be closer to 74–78%.",
  reasoning:
    "Three consecutive CPI prints have come in below expectations, and Fed chair comments have shifted measurably toward a pivot. Futures markets haven't fully caught up.",
};

// ─── Component ──────────────────────────────────────────────────────────────

export default function LandingPage() {
  const { data: picksData, isLoading } = useListPicks({ limit: 3 });
  const picks = picksData?.picks;
  const [demoOpen, setDemoOpen] = useState(false);
  const { isSignedIn } = useUser();
  const [checkoutLoading, setCheckoutLoading] = useState<'starter' | 'pro' | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Capture UTM parameters from the URL (e.g. utm_source=producthunt) and
  // persist them to localStorage for attribution across the session.
  useUtmCapture();
  // Capture a referral code (?ref=CODE) the same way, so it survives the
  // Clerk sign-in modal and can be attributed to the referrer after signup.
  useReferralCapture();

  // Funnel step 1: a real visitor landed on the marketing page.
  useEffect(() => {
    trackEvent(FUNNEL_EVENTS.LANDING_PAGE_VIEWED);
  }, []);

  const handleCheckout = async (tier: 'starter' | 'pro') => {
    setCheckoutLoading(tier);
    setCheckoutError(null);
    trackEvent(FUNNEL_EVENTS.CHECKOUT_STARTED, { tier });
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ targetTier: tier, utm: getStoredUtmParams() ?? undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setCheckoutError(body.error ?? 'Checkout is unavailable right now. Please try again later.');
        return;
      }
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch {
      setCheckoutError('Checkout is unavailable right now. Please try again later.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
            <Sparkles className="h-4 w-4" />
            AI-Powered Prediction Market Analysis
          </div>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
            Predict Markets with
            <br />
            <span className="gradient-text">AI Confidence</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            Claude analyzes live Polymarket and Kalshi markets in seconds — finding pricing inefficiencies
            before the crowd does. Track positions, set alerts, and trade smarter.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <SignInButton mode="modal">
              <Button
                size="lg"
                className="text-lg px-8 py-6 h-auto shadow-lg hover:shadow-xl transition-shadow"
                onClick={() => trackEvent(FUNNEL_EVENTS.SIGNUP_STARTED, { cta: 'hero' })}
              >
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </SignInButton>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto"
              onClick={() => setDemoOpen(true)}
            >
              <Sparkles className="mr-2 h-5 w-5 text-primary" />
              See a Live Demo
            </Button>
          </div>

          <p className="text-sm text-muted-foreground mt-4">
            No credit card required · 5 free analyses/month · Instant access
          </p>
        </div>

        {/* Stats bar */}
        <div className="border-y border-border bg-white/70 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <BarChart2 className="h-4 w-4 text-primary" />
                <span><strong className="text-foreground">Polymarket + Kalshi</strong> markets</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Zap className="h-4 w-4 text-amber-500" />
                <span>Analysis in <strong className="text-foreground">under 30 seconds</strong></span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Shield className="h-4 w-4 text-emerald-500" />
                <span><strong className="text-foreground">Free tier</strong> — no card needed</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 text-blue-500" />
                <span>Prices updated <strong className="text-foreground">every 20–60s</strong></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold mb-3">How it works</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            From live market data to an AI-powered trade recommendation in three steps.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* connector line */}
          <div className="hidden md:block absolute top-8 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-primary/30 via-primary/60 to-primary/30" />
          {[
            {
              step: '01',
              icon: TrendingUp,
              color: 'bg-primary/10 text-primary',
              title: 'Browse live markets',
              desc: 'Search thousands of active Polymarket and Kalshi markets with real-time prices.',
            },
            {
              step: '02',
              icon: Sparkles,
              color: 'bg-violet-100 text-violet-600',
              title: 'Run AI analysis',
              desc: 'Claude evaluates the market, finds the edge, and gives you a clear YES or NO recommendation.',
            },
            {
              step: '03',
              icon: Wallet,
              color: 'bg-emerald-100 text-emerald-600',
              title: 'Track your trades',
              desc: 'Log positions, monitor P&L, and let price alerts keep you ahead of every move.',
            },
          ].map(({ step, icon: Icon, color, title, desc }) => (
            <div key={step} className="text-center relative">
              <div className={`h-16 w-16 rounded-2xl ${color} flex items-center justify-center mx-auto mb-5 shadow-sm`}>
                <Icon className="h-8 w-8" />
              </div>
              <div className="text-xs font-mono text-muted-foreground mb-2">{step}</div>
              <h3 className="text-lg font-bold mb-2">{title}</h3>
              <p className="text-muted-foreground text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features grid ────────────────────────────────────────────── */}
      <div className="bg-slate-50 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold mb-3">Everything you need to trade smarter</h2>
            <p className="text-muted-foreground">Six powerful tools built for serious prediction market traders.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Brain,
                iconBg: 'bg-primary/10 text-primary',
                title: 'Claude AI Analysis',
                desc: 'Full reasoning, edge summary, risk level, target price, and a game plan — not just a signal.',
              },
              {
                icon: MessageSquare,
                iconBg: 'bg-violet-100 text-violet-600',
                title: 'AI Follow-up Chat',
                desc: 'Ask Claude follow-up questions on any analysis. Dive deeper or challenge the reasoning.',
              },
              {
                icon: Bookmark,
                iconBg: 'bg-blue-100 text-blue-600',
                title: 'Watchlist',
                desc: 'Save markets you\'re watching. One tap to analyze when you\'re ready to trade.',
              },
              {
                icon: Bell,
                iconBg: 'bg-amber-100 text-amber-600',
                title: 'Price Alerts',
                desc: 'Set custom price thresholds per market. Get emailed the moment a market crosses your target.',
              },
              {
                icon: Wallet,
                iconBg: 'bg-emerald-100 text-emerald-600',
                title: 'Portfolio Tracking',
                desc: 'Log positions across both platforms. Track P&L, win rate, and AI recommendation accuracy.',
              },
              {
                icon: Share2,
                iconBg: 'bg-rose-100 text-rose-600',
                title: 'Shareable Analyses',
                desc: 'Share any analysis with a public link — perfect for discussing trade ideas with others.',
              },
            ].map(({ icon: Icon, iconBg, title, desc }) => (
              <Card key={title} className="card-elevated border-0">
                <CardContent className="p-6">
                  <div className={`h-12 w-12 rounded-xl ${iconBg} flex items-center justify-center mb-4`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* ── Live Picks ───────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">Live AI Picks</h2>
          <p className="text-muted-foreground">
            Real, recent recommendations from Claude — updated continuously.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : picks && picks.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-6">
            {picks.slice(0, 3).map((pick, idx) => (
              <Card key={idx} className="card-elevated border-0">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <PlatformBadge platform={pick.platform} />
                    <RecommendationBadge recommendation={pick.recommendation as 'YES' | 'NO'} size="sm" />
                  </div>
                  <h3 className="font-semibold mb-4 line-clamp-2">{pick.marketQuestion}</h3>
                  <ConfidenceBar confidence={pick.confidence} className="mb-4" />
                  <p className="text-sm text-muted-foreground line-clamp-2">{pick.edgeSummary}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">No picks available right now</div>
        )}

        <div className="text-center mt-8">
          <SignInButton mode="modal">
            <Button variant="outline" size="lg">
              View All Picks <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </SignInButton>
        </div>
      </div>

      {/* ── Pricing ──────────────────────────────────────────────────── */}
      <div className="bg-slate-50 border-y border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold mb-3">Simple pricing</h2>
            <p className="text-muted-foreground">Start for free. Upgrade when you need more.</p>
          </div>
          {checkoutError && (
            <div className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
              <span className="shrink-0 font-semibold">⚠</span>
              <span>{checkoutError}</span>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <Card className="card-elevated border-0">
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Free</div>
                  <div className="text-4xl font-bold">$0</div>
                  <div className="text-muted-foreground text-sm mt-1">forever</div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    '5 AI analyses per month',
                    'Polymarket + Kalshi browsing',
                    'Portfolio tracking',
                    'Price alerts',
                    'Watchlist',
                    'Shareable links',
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <SignInButton mode="modal">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => trackEvent(FUNNEL_EVENTS.SIGNUP_STARTED, { cta: 'pricing_free' })}
                  >
                    Get started free
                  </Button>
                </SignInButton>
              </CardContent>
            </Card>

            {/* Starter */}
            <Card className="card-elevated border-0 border border-emerald-200">
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-sm font-medium text-emerald-600 mb-1">Starter</div>
                    <div className="text-4xl font-bold">$12</div>
                    <div className="text-muted-foreground text-sm mt-1">per month</div>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    '25 AI analyses per month',
                    'Everything in Free',
                    'AI follow-up chat',
                    'Email notifications',
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isSignedIn ? (
                  <Button
                    variant="outline"
                    className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => handleCheckout('starter')}
                    disabled={checkoutLoading !== null}
                  >
                    {checkoutLoading === 'starter' ? (
                      <><Spinner size="sm" className="mr-2" />Opening Checkout…</>
                    ) : (
                      'Get Starter'
                    )}
                  </Button>
                ) : (
                  <SignInButton mode="modal" fallbackRedirectUrl="/settings?checkout=starter" forceRedirectUrl="/settings?checkout=starter">
                    <Button
                      variant="outline"
                      className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => trackEvent(FUNNEL_EVENTS.SIGNUP_STARTED, { cta: 'pricing_starter' })}
                    >
                      Get Starter
                    </Button>
                  </SignInButton>
                )}
              </CardContent>
            </Card>

            {/* Pro */}
            <Card className="border-2 border-primary shadow-lg shadow-primary/10">
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-sm font-medium text-primary mb-1">Pro</div>
                    <div className="text-4xl font-bold">$29</div>
                    <div className="text-muted-foreground text-sm mt-1">per month</div>
                  </div>
                  <div className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    Best value
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Unlimited AI analyses',
                    'Everything in Starter',
                    'Priority analysis queue',
                    'Early access to new features',
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isSignedIn ? (
                  <Button
                    className="w-full"
                    onClick={() => handleCheckout('pro')}
                    disabled={checkoutLoading !== null}
                  >
                    {checkoutLoading === 'pro' ? (
                      <><Spinner size="sm" className="mr-2" />Opening Checkout…</>
                    ) : (
                      <>Get Pro <ChevronRight className="ml-1 h-4 w-4" /></>
                    )}
                  </Button>
                ) : (
                  <SignInButton mode="modal" fallbackRedirectUrl="/settings?checkout=pro" forceRedirectUrl="/settings?checkout=pro">
                    <Button
                      className="w-full"
                      onClick={() => trackEvent(FUNNEL_EVENTS.SIGNUP_STARTED, { cta: 'pricing_pro' })}
                    >
                      Get Pro <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </SignInButton>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h2 className="text-4xl font-bold mb-4">
          Start finding edges <span className="gradient-text">today</span>
        </h2>
        <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
          Join traders using MarketMind to analyze markets faster and more rigorously than the competition.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <SignInButton mode="modal">
            <Button
              size="lg"
              className="text-lg px-8 py-6 h-auto shadow-lg"
              onClick={() => trackEvent(FUNNEL_EVENTS.SIGNUP_STARTED, { cta: 'bottom' })}
            >
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </SignInButton>
          <Button size="lg" variant="outline" className="text-lg px-8 py-6 h-auto" onClick={() => setDemoOpen(true)}>
            <Sparkles className="mr-2 h-5 w-5 text-primary" />
            Try the Demo
          </Button>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
                <Brain className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg">MarketMind</span>
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              ⚠️ MarketMind is not a financial advisor. All analysis is for educational purposes only.
              Prediction markets involve real risk — trade responsibly.
            </p>
            <div className="flex gap-3 text-sm text-muted-foreground items-center flex-wrap justify-center">
              <a
                href="https://www.producthunt.com/posts/marketmind"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-orange-500 transition-colors"
                title="Upvote MarketMind on Product Hunt"
              >
                🚀 Product Hunt
              </a>
              <span>·</span>
              <a
                href={`https://twitter.com/intent/tweet?text=Check+out+MarketMind+%E2%80%94+AI-powered+prediction+market+analysis&url=${encodeURIComponent(CAMPAIGN_URLS.twitter)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sky-500 transition-colors"
                title="Share on X / Twitter"
              >
                Share on X
              </a>
              <span>·</span>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(CAMPAIGN_URLS.linkedin)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-600 transition-colors"
                title="Share on LinkedIn"
              >
                Share on LinkedIn
              </a>
              <span>·</span>
              <Link href="/privacy" className="hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <span>·</span>
              <Link href="/terms" className="hover:text-foreground transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Demo modal */}
      <DemoAnalysisModal open={demoOpen} onClose={() => setDemoOpen(false)} analysis={DEMO_ANALYSIS} />
    </div>
  );
}
